import { Dispatch, FC, SetStateAction, useState } from 'react';

import { message } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import { UploadFile, UploadProps } from 'antd/lib';

import {
  ColSize,
  DraggerUploadFile,
  RowSize,
} from '../../../../../../../../../shared/components';
import { Modal } from '@hdi-seguros/web-components.modal';
import { ActionButton } from '@hdi-seguros/web-components.action-button';

interface UploadFileModalProps {
  canShowUploadModalUploadFile: boolean;
  setCanShowUploadModalUploadFile: Dispatch<SetStateAction<boolean>>;
  setFileSelect: Dispatch<SetStateAction<any[]>>;
  openFile: any;
  singleFile?: boolean;
}

export const UploadFileModal: FC<UploadFileModalProps> = ({
  canShowUploadModalUploadFile,
  setCanShowUploadModalUploadFile,
  setFileSelect,
  openFile,
  singleFile = false,
}) => {
  const [fileSelectUpload, setFileSelectUpload] = useState<any[]>([]);



  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];
  const MAX_FILE_SIZE = 2 * 1024 * 1024;

  const fileToBase64 = (file: Blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (event) => {
        const errorMessage = `Error al cargar el archivo: ${event?.target?.error?.message}`;
        reject(new Error(errorMessage));
      };
    });
  };

  const validateFile = (file: { name: string; type: string; size: number }): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `El archivo ${file.name} no es válido. Solo se permiten archivos PNG, JPG, JPEG o PDF.`;
    }

    if (file.size > MAX_FILE_SIZE) {
      return `El archivo ${file.name} supera el tamaño máximo de 2MB.`;
    }

    return null;
  };

  const handleFileSelection = async (
    info: UploadChangeParam<UploadFile<any>>
  ) => {
    if (info.file.status === 'removed') {
      setFileSelectUpload([]);
      return;
    }

    const file = info.file;

    if (file.originFileObj instanceof Blob) {
      const validationError = validateFile({
        name: file.name,
        type: file.originFileObj.type,
        size: file.originFileObj.size,
      });

      if (validationError) {
        message.error(validationError);
        return;
      }

      const base64 = await fileToBase64(file.originFileObj);
      setFileSelectUpload([
        {
          uid: file.uid,
          name: file.name,
          mediaType: file.type,
          base: base64,
          type: info.file.type?.split('/')[1],
        },
      ]);
    }
  };

  const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    const droppedFiles = Array.from(event.dataTransfer.files);
    const filesToProcess = singleFile ? droppedFiles.slice(0, 1) : droppedFiles;

    const validFiles: File[] = [];
    filesToProcess.forEach((file) => {
      const validationError = validateFile(file);

      if (validationError) {
        message.error(validationError);
        return;
      }

      validFiles.push(file);
    });

    if (!validFiles.length) return;

    try {
      const fileList = await Promise.all(
        validFiles.map(async (file) => {
          const base64 = await fileToBase64(file);
          return {
            uid: `${file.name}-${file.lastModified}`,
            name: file.name,
            mediaType: file.type,
            base: base64,
            type: file.type?.split('/')[1],
          };
        })
      );

      setFileSelectUpload(fileList);
    } catch (error) {
      message.error('Error al cargar los archivos');
    }
  };

  const draggerProps: UploadProps = {
    name: 'file',
    multiple: true,
    customRequest: () => false,
    onChange: (info) => {
      handleFileSelection(info);
    },
    onDrop: (e) => {
      handleFileDrop(e);
    },
  };

  const handleConfirm = () => {
    setCanShowUploadModalUploadFile(false);
    setFileSelect(fileSelectUpload);
    setFileSelectUpload([]);
  };

  const handleCancel = () => {
    setCanShowUploadModalUploadFile(false);
    setFileSelectUpload([]);
  };

  return (
    <Modal
      centered
      footer={
        <>
          <ActionButton
            className='btn-principal-outline-barbuss'
            label='Cancelar'
            type="secondary"
            onClick={handleCancel}
          />

          <ActionButton 
            className='btn-principal-barbuss'
            label='Confirmar'
            type="primary"
            onClick={handleConfirm}
            disabled={!fileSelectUpload.length}
          />
        </>
      }
      onCancel={handleCancel}
      open={canShowUploadModalUploadFile}
      title='Carga de Comprobantes'
      width={650}
    >
      <RowSize className='mb-1' rowProps={{ gutter: [0, 0] }}>
        <ColSize size={100}>
          <RowSize rowProps={{ gutter: [0, 0], justify: 'center' }}>
            <DraggerUploadFile
              draggerProps={draggerProps}
              fileList={fileSelectUpload}
              onPreview={(file) => openFile(file)}
              formatFile={'PNG, JPG, JPEG O PDF'}
            />
          </RowSize>
        </ColSize>
      </RowSize>
    </Modal>
  );
};
